import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * Getting in (the access module, shared like cash): invite links and requests for an account or
 * an app. They live outside the Admin app so that a branch lead who invites and approves their
 * own people never gets the Admin app with it. Nobody ever picks permissions: an account gets
 * roles — the position's by default — and whoever gives them may only give roles they may grant
 * (`admin.role.grantableBy`); a super admin gives any.
 */

/** `join`: a new account (and employee); `reset`: a new password for an existing account (no e-mail needed) */
export const inviteKinds = ['join', 'reset'] as const
export const inviteStatuses = ['active', 'used', 'revoked', 'expired'] as const

/**
 * access.invite (ลิงก์เชิญ): a one-time link or QR, valid for some hours. `join`: the person
 * chooses a username and password and starts at once with the roles, branch and position it
 * carries (no approval: the inviter vouched for them); their employee is the one given, or one
 * is drafted for HR to complete. `reset` (permission `reset`): sets a new password for `userId`;
 * its branch is that user's, so a lead resets only their own branch's people.
 * Scope (`@branch`) is the invite's branch: a lead invites only into their own branch.
 */
export const accessInvite = defineResource({
  app: 'access',
  name: 'invite',
  path: 'invites',
  schema: z.object({
    kind: z.enum(inviteKinds).default('join'),
    /** Who it is for, as the link page greets them ("สวัสดีคุณสมชาย") */
    name: z.string().trim().max(120).nullish(),
    branchId: z.string().nullish(),
    positionId: z.string().nullish(),
    /** join: roles of the new account; empty → the position's */
    roleIds: z.array(z.string()).default([]),
    /** join: the employee the account belongs to (else one is drafted) */
    employeeId: z.string().nullish(),
    /** join: a contract driver's record the account belongs to (no job application, no employee) */
    driverId: z.string().nullish(),
    /** reset: whose password */
    userId: z.string().nullish(),
    expiresInHours: z.number().int().min(1).max(168).default(48),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    kind: { filter: true, list: { width: 140, priority: 1 }, form: { span: 4, custom: true } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 } },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6, custom: true },
    },
    positionId: {
      type: 'ref',
      ref: { resource: 'hr.position', where: { status: 'active' } },
      list: { priority: 2 },
      form: { span: 6, custom: true },
    },
    roleIds: { type: 'multiselect', ref: { resource: 'admin.role' }, list: { priority: 2 }, form: { span: 12, custom: true } },
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, list: { priority: 3, hidden: true }, form: { span: 6, custom: true } },
    driverId: {
      type: 'ref',
      ref: { resource: 'tms.driver', where: { contractor: 'true' } },
      list: { priority: 3, hidden: true },
      form: { span: 6, custom: true },
    },
    userId: { type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true }, form: { span: 6, custom: true } },
    expiresInHours: { type: 'integer', list: false, form: { span: 6 } },
    /** The secret in the link (server-made), shown only to whoever may read the invite */
    token: { readOnly: true, list: false },
    expiresAt: { readOnly: true, type: 'datetime', list: { width: 160, priority: 2 } },
    status: {
      readOnly: true,
      type: 'select',
      options: inviteStatuses,
      filter: true,
      tones: { active: 'info', used: 'success', revoked: 'neutral', expired: 'neutral' },
      list: { width: 120, priority: 1 },
    },
    usedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    usedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    createdByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 2 } },
    createdAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  columns: ['name', 'kind', 'branchId', 'positionId', 'status', 'expiresAt', 'roleIds', 'createdByUserId', 'usedAt'],
  defaultSort: '-createdAt',
  // `revoke`: stop a link not used yet; `reset`: make password reset links
  actions: ['read', 'create', 'delete', 'revoke', 'reset'],
  branchFields: ['branchId'],
  card: { title: 'name', subtitle: 'kind', meta: ['branchId', 'expiresAt'], status: 'status' },
})

/** `signup`: someone without an account asks for one (with the branch's join code); `app`: a user asks to use an app */
export const accessRequestKinds = ['signup', 'app'] as const
export const accessRequestStatuses = ['pending', 'approved', 'rejected', 'cancelled'] as const

/**
 * access.request (คำขอ): made on the sign-up page or in Thananan OS ("แอพในบริษัท"), never with
 * the generic form. Approved (or rejected, with a reason) by anyone holding `approve` whose
 * scope covers it — the requester's branch for a lead, all for HR or an admin — or by the
 * requester's own manager (hr.employee.managerId); never by the requester. The approver picks
 * the roles to give, only ones they may grant. A sign-up's account exists from the start but
 * cannot sign in until approved ("รออนุมัติ"); approving opens it and drafts their employee.
 */
export const accessRequest = defineResource({
  app: 'access',
  name: 'request',
  path: 'requests',
  schema: z.object({
    kind: z.enum(accessRequestKinds).default('app'),
    /** app: which app (an AppId) */
    appId: z.string().nullish(),
    branchId: z.string().nullish(),
    positionId: z.string().nullish(),
    reason: z.string().trim().max(500).nullish(),
  }),
  fields: {
    kind: { filter: true, list: { width: 130, priority: 1 } },
    /** The requester: the asking user, or the account a sign-up made */
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 3, hidden: true } },
    applicantName: { readOnly: true, search: true, list: { priority: 1 } },
    applicantPhone: { readOnly: true, type: 'phone', list: { priority: 2 } },
    username: { readOnly: true, list: { priority: 3, hidden: true } },
    appId: { filter: true, list: { width: 130, priority: 1 }, form: { custom: true } },
    branchId: { type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 1 } },
    positionId: { type: 'ref', ref: { resource: 'hr.position' }, list: { priority: 2 } },
    reason: { list: { priority: 2 } },
    requestedAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    status: {
      readOnly: true,
      type: 'select',
      options: accessRequestStatuses,
      filter: true,
      tones: { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'neutral' },
      list: { width: 120, priority: 1 },
    },
    grantedRoleIds: { readOnly: true, type: 'multiselect', ref: { resource: 'admin.role' }, list: { priority: 3, hidden: true } },
    reviewedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    reviewedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    rejectReason: { readOnly: true, list: false },
    /** The employee it was linked to, or drafted, on approval */
    employeeId: { readOnly: true, type: 'ref', ref: { resource: 'hr.employee' }, list: false },
  },
  title: 'applicantName',
  columns: [
    'requestedAt',
    'applicantName',
    'kind',
    'appId',
    'branchId',
    'positionId',
    'status',
    'reason',
    'applicantPhone',
    'reviewedByUserId',
  ],
  defaultSort: '-requestedAt',
  // `approve` covers rejecting too
  actions: ['read', 'export', 'approve'],
  ownerField: 'userId',
  branchFields: ['branchId'],
  card: { title: 'applicantName', subtitle: 'kind', meta: ['requestedAt', 'branchId'], status: 'status' },
})
